import { SOURCE_IMPORT_DUPLICATE_SOURCE_ERROR } from '../config/sourceImport';
import { MISSING_STATUS_REASON } from '../config/statusCoordinator';
import type { UploadTask } from '../types/uploads';
import { isTerminalPhase } from './uploadLifecycle';

/** A failed MCAP can still expose the backend-only Recheck command after a reload. */
function isRecoverableMcapFailure(item: UploadTask): boolean {
  return (
    item.phase === 'failed' &&
    item.channel === 'upload' &&
    item.mediaType === 'mcap' &&
    Boolean(item.backendId) &&
    Boolean(item.mcapTaskId) &&
    item.mcapCheckGeneration !== undefined &&
    item.mcapRecheckable === true
  );
}

/** Server history owns ordinary terminal rows; browser persistence keeps only actionable work. */
export function shouldPersistUploadTask(item: UploadTask): boolean {
  return !isTerminalPhase(item.phase) || isRecoverableMcapFailure(item);
}

export function retainPersistedUploadTasks(items: readonly UploadTask[]): UploadTask[] {
  return items.filter(shouldPersistUploadTask);
}

function isLegacySourceImportOrphan(item: UploadTask): boolean {
  if (item.channel !== 'import' || item.phase !== 'error') return false;
  if (item.reason === SOURCE_IMPORT_DUPLICATE_SOURCE_ERROR) return true;
  return (
    item.statusSyncError === true &&
    item.reason === MISSING_STATUS_REASON &&
    item.mediaType === undefined &&
    item.bytes === 0 &&
    item.totalBytes === 0 &&
    item.importStage === undefined &&
    item.progress === undefined &&
    item.intakeStarted === undefined &&
    item.assetId === undefined
  );
}

/** One-time cleanup for source-import rows whose create request never produced a backend row. */
export function migratePersistedUploadTasks(items: readonly UploadTask[]): UploadTask[] {
  return retainPersistedUploadTasks(items).filter((item) => !isLegacySourceImportOrphan(item));
}

/** Strip runtime-only fields before JSON serialization. */
export function serializeUploadTask(item: UploadTask): UploadTask {
  return {
    id: item.id,
    createdAt: item.createdAt,
    channel: item.channel,
    uploadRoute: item.uploadRoute,
    mediaType: item.mediaType,
    target: item.target,
    phase: item.phase,
    displayName: item.displayName,
    bytes: item.bytes,
    totalBytes: item.totalBytes,
    backendId: item.backendId,
    sourceUrl: item.sourceUrl,
    contentBlake3: item.contentBlake3,
    assetId: item.assetId,
    dedupPrecheckOutcome: item.dedupPrecheckOutcome,
    reason: item.reason,
    mcapFailureCode: item.mcapFailureCode,
    statusSyncError: item.statusSyncError,
    rejectKind: item.rejectKind,
    importStage: item.importStage,
    progress: item.progress,
    intakeStarted: item.intakeStarted,
    attemptLabel: item.attemptLabel,
    mcapCheckGeneration: item.mcapCheckGeneration,
    mcapTaskId: item.mcapTaskId,
    mcapRecheckable: item.mcapRecheckable,
    mcapDescriptorWarnings: item.mcapDescriptorWarnings,
    mcapRecheckRequestId: item.mcapRecheckRequestId,
    needsRestart: item.needsRestart,
  };
}

export function serializePersistedUploadTasks(items: readonly UploadTask[]): UploadTask[] {
  return retainPersistedUploadTasks(items).map(serializeUploadTask);
}
